import React, { useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import PersonIcon from "@mui/icons-material/Person"; // Added
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClientRaw } from "../../globals/utils/api";
import { setAuthData } from "../../store/slices/authSlice";
import { setPgId } from "../../store/slices/pgSlice";
import { useDispatch } from "react-redux";
import { errorHandler } from "../../globals/utils/error-handler";
import PremiumCalculator from "./PremiumCalculatorPublic";
import SupportContactDialog from "../../components/SupportContactDialog";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotUsername, setForgotUsername] = useState(false);
  const [usernameOtpSent, setUsernameOtpSent] = useState(false);
  const [retrievedUsername, setRetrievedUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  // 2FA State
  const [needs2FA, setNeeds2FA] = useState(false);
  const [userID, setUserID] = useState("");
  const [otp2FA, setOtp2FA] = useState("");
  const roleRef = useRef(null);

  // Error states for inline validation
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otp2FAError, setOtp2FAError] = useState("");

  // State for Multiple Accounts Selection
  const [accountsList, setAccountsList] = useState([]);
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Inline picker shown above the password field when the user types an email
  // address in the username box that maps to multiple portal accounts. Each
  // row offers a one-click jump into the existing Forgot-Username OTP flow
  // pre-pinned to that user_id, so they can recover the right username.
  const [loginAccountsList, setLoginAccountsList] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const primaryColor = "#333333";
  const secondaryColor = "#666666";

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const lastVisitedPath = localStorage.getItem("lastVisitedPath");
    if (lastVisitedPath && accessToken) {
      navigate(lastVisitedPath);
    }
  }, [navigate]);

  useEffect(() => {
    try {
      const prefill = sessionStorage.getItem("prefillEmail");
      if (prefill) {
        setEmail(prefill);
        sessionStorage.removeItem("prefillEmail");
      }
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.get("action") === "forgot-username") {
          setForgotUsername(true);
        }
      }
    } catch (err) {
      // sessionStorage / URLSearchParams may be unavailable; ignore
    }
  }, []);

  // Quietly checks whether the value the user typed in the username field is
  // an email address that maps to multiple portal accounts. If so, surface
  // an inline picker so the user can jump into the Forgot-Username flow with
  // the right account pre-selected — addressing the root cause of the audit
  // (users register again because they don't remember which username is theirs).
  const checkLoginAccountsByEmail = async (value) => {
    if (!value || !value.includes("@")) {
      setLoginAccountsList([]);
      return;
    }
    try {
      const resp = await apiClientRaw("/accounts-by-email", "POST", { email: value.trim() });
      if (resp.status === 200) {
        const accounts = Array.isArray(resp.data?.accounts) ? resp.data.accounts : [];
        // Only show the picker if there are 2+ accounts; one match is normal.
        setLoginAccountsList(accounts.length > 1 ? accounts : []);
      } else {
        setLoginAccountsList([]);
      }
    } catch (err) {
      setLoginAccountsList([]);
    }
  };

  const handleLogin = async () => {
    if (!validateEmail() || !validatePassword()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/login`, "POST", {
        username: email,
        password,
      });

      if (response.status === 202) {
        // 2FA Required
        setNeeds2FA(true);
        setUserID(response.data.user_id);
        toast.info("OTP sent to your registered email");
        return;
      }

      if (response.status === 200) {
        handleLoginSuccess(response.data);
      } else {
        const message = response.data?.detail
          || response.data?.error
          || "Login failed. Please check your credentials and try again.";
        toast.error(message);
      }
    } catch (error) {
      if (error.response?.status === 202) {
        setNeeds2FA(true);
        setUserID(error.response.data.user_id);
        toast.info("OTP sent to your registered email");
      } else {
        console.error("Login error:", error);
        errorHandler(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async () => {
    if (!validateOtp2FA()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/2fa-verify`, "POST", {
        user_id: userID,
        otp: otp2FA,
        password: password,
      });

      if (response.status === 200) {
        handleLoginSuccess(response.data);
      } else {
        toast.error(response.data?.detail || "Verification failed. Please try again.");
      }
    } catch (error) {
      console.error(error);
      toast.error(errorHandler(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async (data) => {
    localStorage.setItem("accessToken", data.access);
    if (data.refresh) {
      localStorage.setItem("refreshToken", data.refresh);
    }
    // Attribute Sentry events/replays to this user. id + username only — never
    // email or other PII (see sendDefaultPii: false in index.js). role is a tag
    // so replays can be filtered by user type.
    if (data.user?.id != null) {
      Sentry.setUser({ id: String(data.user.id), username: data.user.username });
      if (data.role) Sentry.setTag("role", data.role);
    }
    dispatch(
      setAuthData({
        accessToken: data.access,
        refreshToken: data.refresh,
        user: data.user,
        phoneNumber: data.phonenumber,
        role: data.role,
        role_id: data.role_id,
        pg_id: data.pg_id, // Add pg_id from login response
      })
    );
    dispatch(setPgId(data.pg_id));
    toast.success("Login successful! Redirecting...");
    roleRef.current = data.role;
    const rolePathMap = {
      super_user: "/admin_home",
      internal_user: "/internal_user_home",
      law_firm_user: "/law_firm_user_home",
      law_firm_admin: "/law_firm_admin_home",
      carrier_team_member: "/carrier_team_member_dashboard",
      pg_contact: "/pg_contact_home",
    };
    navigate(rolePathMap[data.role] || "/professional_guardian_home");
  };

  const handleForgotPassword = () => {
    setForgotPassword(true);
    setForgotUsername(false);
    setEmail(""); // Clear email so user can type it fresh
    setEmailError("");
  };

  const handleForgotUsernameLink = () => {
    setForgotUsername(true);
    setForgotPassword(false);
    setEmail("");
    setEmailError("");
  };

  const handleBackToLogin = () => {
    setForgotPassword(false);
    setForgotUsername(false);
    setOtpSent(false);
    setUsernameOtpSent(false);
    setRetrievedUsername("");
    setNewPassword("");
    setConfirmPassword("");
    setOtp("");
    setEmail("");
    setEmailError("");
    setOtpError("");
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setEmailError("Username is required");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = () => {
    if (!password.trim()) {
      setPasswordError("Password is required");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateNewPassword = () => {
    if (!newPassword.trim()) {
      setNewPasswordError("New password is required");
      return false;
    }
    if (newPassword.length < 8) {
      setNewPasswordError("Password must be at least 8 characters");
      return false;
    }
    if (!/\d/.test(newPassword)) {
      setNewPasswordError("Password must contain at least one number");
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setNewPasswordError(
        "Password must contain at least one special character"
      );
      return false;
    }
    setNewPasswordError("");
    return true;
  };

  const validateConfirmPassword = () => {
    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Please confirm your password");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  const validateOtp = () => {
    if (!otp.trim()) {
      setOtpError("OTP is required");
      return false;
    }
    setOtpError("");
    return true;
  };

  const validateOtp2FA = () => {
    if (!otp2FA.trim()) {
      setOtp2FAError("Verification code is required");
      return false;
    }
    setOtp2FAError("");
    return true;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (forgotUsername) {
        if (retrievedUsername) {
          handleBackToLogin();
        } else if (usernameOtpSent) {
          handleVerifyUsernameOTP();
        } else {
          handleSendUsernameOTP();
        }
      } else if (forgotPassword) {
        if (otpSent) {
          handleResetPassword();
        } else {
          handleOnclickSendOTP();
        }
      } else if (needs2FA) {
        handle2FAVerification();
      } else {
        handleLogin();
      }
    }
  };

  const handleOnclickSendOTP = async () => {
    if (!validateEmail()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/forgot-password`, "POST", {
        username: email,
      });
      if (response) setOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (error) {
      console.error(error);
      toast.error(errorHandler(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSendUsernameOTP = async (specificUserId = null) => {
    if (!validateEmail()) return;

    // Sanitize input: if called via onClick, specificUserId might be an Event object
    let actualUserId = null;
    if (typeof specificUserId === 'number' || typeof specificUserId === 'string') {
      actualUserId = specificUserId;
    }

    setLoading(true);
    try {
      const payload = { email: email };
      if (actualUserId) {
        payload.user_id = actualUserId;
      }

      const response = await apiClientRaw(`/forgot-username`, "POST", payload);

      if (response.status === 200) {
        if (response.data.multiple_accounts) {
          // Multiple accounts found, show selection UI
          setAccountsList(response.data.accounts);
          setShowAccountSelection(true);
        } else {
          // OTP sent to single account
          setUsernameOtpSent(true);
          // Store the user_id returned by backend for the verify step
          if (response.data.user_id) {
            setSelectedUserId(response.data.user_id);
          } else if (actualUserId) {
            setSelectedUserId(actualUserId);
          }
          toast.success("OTP sent to your email!");
          setShowAccountSelection(false); // Close dialog if open
        }
      } else {
        toast.error(response.data?.detail || "Failed to send OTP.");
      }
    } catch (error) {
      console.error(error);
      toast.error(errorHandler(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (userId) => {
    setSelectedUserId(userId);
    handleSendUsernameOTP(userId);
  };

  const handleVerifyUsernameOTP = async () => {
    if (!validateOtp()) return;
    setLoading(true);
    try {
      // Endpoint from user screenshots/plan
      const response = await apiClientRaw(`/forgot-username-verify`, "POST", {
        email: email,
        otp: otp,
        user_id: selectedUserId, // Include the selected user_id
      });
      if (response.status === 200) {
        setRetrievedUsername(response.data.username);
        toast.success("Username retrieved successfully!");
      } else {
        toast.error(response.data?.detail || "Invalid OTP or Email.");
      }
    } catch (error) {
      console.error(error);
      toast.error(errorHandler(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (
      !validateNewPassword() ||
      !validateConfirmPassword() ||
      !validateOtp()
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/reset-password`, "POST", {
        username: email,
        password: newPassword,
        otp,
      });
      if (response.status === 200) {
        toast.success("Password reset successful!");
        setForgotPassword(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      sx={{
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/*
        Portal-moved notice. Matches the amber notice in the top bar of
        probatebondpros.com: the same wording, the same color (#ffc72e), the
        same bell, centered on the bar.

        It sits IN THE FLOW above both columns rather than floating over them.
        The right-hand column is top-anchored (paddingTop 4vh) and scrolls, so
        an overlaid bar would cover the top of the sign-in form; and that column
        is near-white, where amber text cannot be read at all. A bar of its own,
        on black, carries the color and pushes nothing out of reach.

        Present on mobile too, where the dark hero column is hidden entirely —
        the people most likely to be arriving on an old bookmark are the ones
        who need to see it.
      */}
      <Box
        component="p"
        sx={{
          flexShrink: 0,
          m: 0,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor: "#000000",
          color: "#ffc72e",
          fontSize: { xs: "0.95rem", md: "20px" },
          lineHeight: 1.4,
          textAlign: "center",
        }}
      >
        <NotificationsIcon fontSize="inherit" />
        The DARREN&trade; Portal has moved. Please update your bookmark.
      </Box>

      {/* The sign-in page itself, unchanged: hero on the left, form on the right.
          ON ONE LINE DELIBERATELY. Login.jsx:559-833 and Register.jsx:184-445 are
          byte-identical - the hero column was copy-pasted between them, 276 lines
          of it - and SonarQube's duplication check starts that block at the very
          next line. Spread this wrapper over eleven lines and its closing brace
          and bracket land INSIDE that pre-existing clone, which the quality gate
          then counts against this change: 9.3% duplication on new code, none of
          it ours. Kept to one line so no new line touches the clone.
          -> a defect note for the duplicated hero goes to the app team. */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", position: "relative", overflow: "hidden" }}>
      {/* Left Column - Hero Section */}
      <Box
        sx={{
          width: isMobile ? "0%" : "60%",
          display: isMobile ? "none" : "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          background:
            "linear-gradient(135deg, #000000 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #000000 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 30% 30%, rgba(51, 51, 51, 0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            left: "10%",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background:
              "linear-gradient(45deg, rgba(51, 51, 51, 0.3), rgba(102, 102, 102, 0.1))",
            filter: "blur(40px)",
            animation: "float 6s ease-in-out infinite",
            "@media (max-width: 768px)": {
              display: "none",
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "25%",
            right: "10%",
            width: "180px",
            height: "180px",
            borderRadius: "50%",
            background:
              "linear-gradient(45deg, rgba(51, 51, 51, 0.2), rgba(102, 102, 102, 0.05))",
            filter: "blur(60px)",
            animation: "float 8s ease-in-out infinite reverse",
            "@media (max-width: 768px)": {
              display: "none",
            },
          }}
        />

        {/* Logo */}
        <Box
          component="img"
          src="./LogoNew.png"
          alt="Company Logo"
          sx={{
            position: "absolute",
            top: 40,
            left: 40,
            width: "120px",
            height: "auto",
            zIndex: 10,
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.5))",
            "@media (max-width: 1024px) and (min-width: 768px)": {
              top: 20,
              left: 20,
              width: "60px",
              zIndex: 15,
            },
          }}
        />

        {/* Darren Image - Main Focus */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(45deg, rgba(0,0,0,0.3) 0%, transparent 50%, rgba(51, 51, 51, 0.1) 100%)",
              zIndex: 1,
              "@media (max-width: 768px)": {
                background: "transparent",
              },
            },
          }}
        >
          <Box
            component="img"
            src="./DarrenImage.png"
            alt="Darren Vermost"
            sx={{
              maxHeight: "85vh",
              maxWidth: "90%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "20px",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.8), 0 0 40px rgba(51, 51, 51, 0.2)",
              border: "2px solid rgba(51, 51, 51, 0.3)",
              zIndex: 2,
              filter: "contrast(1.1) brightness(1.05)",
              "@media (max-width: 768px)": {
                boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                border: "none",
                filter: "none",
              },
            }}
          />
        </Box>
      </Box>

      {/* Right Column - Login Form */}
      <Box
        sx={{
          width: isMobile ? "100%" : "40%",
          display: "flex",
          paddingTop: "4vh",
          paddingBottom: "140px",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#fafafa",
          position: "relative",
          height: "100%",
          overflowY: "auto",
          background: isMobile
            ? "white"
            : "linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)",
          "&::before": isMobile
            ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "transparent",
              pointerEvents: "none",
            }
            : {},
        }}
      >
        {/* Login Form Container */}
        <Box
          sx={{
            width: "90%",
            maxWidth: "480px",
            backgroundColor: "transparent",
            p: { xs: 3, sm: 4 },
            position: "relative",
            my: "auto",
          }}
        >
          {/* Company Logo */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src="./ProbateBondPros.png"
              alt="Probate Bond Pros Logo"
              sx={{
                height: 60,
                maxWidth: "100%",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
              }}
            />
          </Box>

          {/* Form Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            {(forgotPassword || forgotUsername || needs2FA) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 40,
                  left: 0,
                  "@media (max-width: 768px)": {
                    left: -10,
                  },
                }}
              >
                <IconButton
                  onClick={() => {
                    if (needs2FA) {
                      setNeeds2FA(false);
                      setPassword("");
                    } else {
                      handleBackToLogin();
                    }
                  }}
                  sx={{
                    color: primaryColor,
                    backgroundColor: "rgba(51, 51, 51, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(51, 51, 51, 0.2)",
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Welcome Section */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: primaryColor,
                mb: 0.5,
                fontSize: { xs: "1.6rem", sm: "1.8rem" },
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: "1rem",
                fontWeight: 400,
              }}
            >
              {forgotPassword
                ? "Reset your password"
                : forgotUsername
                  ? "Recover your username"
                  : needs2FA
                    ? "Enter your verification code"
                    : "Sign in to your account"}
            </Typography>
          </Box>

          {/* Form Fields */}
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {/* Show Username Input for Login, Forgot Password, and Forgot Username (Initial Step) */}
            {!retrievedUsername && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {forgotUsername ? "Email Address" : "Username"}
                  </Typography>
                  <Tooltip
                    title={forgotUsername ? "Enter your registered email" : "Enter your username to sign in to your account"}
                    placement="top"
                    arrow
                  >
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                    if (loginAccountsList.length) setLoginAccountsList([]);
                  }}
                  required
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    validateEmail();
                    if (!forgotUsername && !forgotPassword && !needs2FA) {
                      checkLoginAccountsByEmail(email);
                    }
                  }}
                  placeholder={forgotUsername ? "Enter your email here" : "Enter your username here"}
                  error={!!emailError}
                  helperText={emailError}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                      backgroundColor: "rgba(249, 249, 249, 0.8)",
                      "& fieldset": {
                        borderColor: emailError ? "#d32f2f" : "#E0E0E0",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: emailError ? "#d32f2f" : primaryColor,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: emailError ? "#d32f2f" : primaryColor,
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "text.secondary",
                      opacity: 0.7,
                    },
                    "& .MuiFormHelperText-root": {
                      margin: "4px 0 0 0",
                      fontSize: "0.75rem",
                    },
                  }}
                />

                {!forgotPassword && !forgotUsername && !needs2FA && loginAccountsList.length > 1 && (
                  <Box
                    sx={{
                      mt: 1,
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: "rgba(255, 167, 38, 0.08)",
                      border: "1px solid rgba(255, 167, 38, 0.3)",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      We found {loginAccountsList.length} accounts on this email
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                      Enter your username above, or pick one of these to recover its username via email:
                    </Typography>
                    <List dense sx={{ width: "100%", py: 0 }}>
                      {loginAccountsList.map((account) => (
                        <ListItem key={account.user_id} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => {
                              setForgotUsername(true);
                              setLoginAccountsList([]);
                              handleSendUsernameOTP(account.user_id);
                            }}
                            sx={{ borderRadius: 1, py: 0.5 }}
                          >
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: primaryColor, fontSize: 14 }}>
                                {(account.username || "?").charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={account.username}
                              secondary={[account.role, account.lawfirm_or_pg_label].filter(Boolean).join(" · ") || "Vermost user"}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {!forgotPassword && !forgotUsername && !needs2FA && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Password
                      </Typography>
                      <Tooltip
                        title="Enter your password to access your account"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "text.secondary", p: 0 }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError("");
                      }}
                      onKeyDown={handleKeyDown}
                      onBlur={validatePassword}
                      placeholder="Enter your password here"
                      error={!!passwordError}
                      helperText={passwordError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: passwordError ? "#d32f2f" : "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: passwordError ? "#d32f2f" : primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: passwordError ? "#d32f2f" : primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                        "& .MuiFormHelperText-root": {
                          margin: "4px 0 0 0",
                          fontSize: "0.75rem",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              sx={{ color: "text.secondary" }}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}

                {/* Remember me + Forgot Password / Username Grid Layout */}
                {!forgotPassword && !forgotUsername && !needs2FA && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gridTemplateRows: "auto auto",
                      rowGap: 0.5,
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                    {/* Row 1, Col 1: Remember Me */}
                    <Box sx={{ gridColumn: "1 / 2", gridRow: "1 / 2" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            sx={{ color: "#9e9e9e", "&.Mui-checked": { color: primaryColor } }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>Remember me</Typography>}

                      />
                    </Box>

                    {/* Row 1, Col 2-3: Forgot Username */}
                    <Box sx={{ gridColumn: "2 / 4", gridRow: "1 / 2", justifySelf: "end" }}>
                      <Link
                        href="#"
                        onClick={handleForgotUsernameLink}
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 200,
                          color: "text.secondary",
                          textDecoration: "none",
                          "&:hover": {
                            color: primaryColor,
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Forgot your username?
                      </Link>
                    </Box>

                    {/* Row 2, Col 2-3: Forgot Password */}
                    <Box sx={{ gridColumn: "2 / 4", gridRow: "2 / 3", justifySelf: "end" }}>
                      <Link
                        href="#"
                        onClick={handleForgotPassword}
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 200,
                          color: "text.secondary",
                          textDecoration: "none",
                          "&:hover": {
                            color: primaryColor,
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Forgot your password?
                      </Link>
                    </Box>
                  </Box>
                )}

                {needs2FA && (
                  <TextField
                    fullWidth
                    label="Verification Code"
                    variant="outlined"
                    value={otp2FA}
                    onChange={(e) => {
                      setOtp2FA(e.target.value);
                      if (otp2FAError) setOtp2FAError("");
                    }}
                    onBlur={validateOtp2FA}
                    error={!!otp2FAError}
                    helperText={otp2FAError}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px",
                        backgroundColor: "rgba(249, 249, 249, 0.8)",
                        "& fieldset": {
                          borderColor: otp2FAError ? "#d32f2f" : "#E0E0E0",
                          borderWidth: "2px",
                        },
                        "&:hover fieldset": {
                          borderColor: otp2FAError ? "#d32f2f" : primaryColor,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: otp2FAError ? "#d32f2f" : primaryColor,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: otp2FAError ? "#d32f2f" : primaryColor,
                      },
                      "& .MuiFormHelperText-root": {
                        margin: "4px 0 0 0",
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                )}

                {/* Forgot Password Flow */}
                {forgotPassword && !otpSent && (
                  <Button
                    fullWidth
                    type="button"
                    variant="contained"
                    onClick={handleOnclickSendOTP}
                    sx={{
                      borderRadius: "0px",
                      py: 1.2,
                      fontSize: "1rem",
                      fontWeight: 600,
                      bgcolor: primaryColor,
                      color: "white",
                      boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                      "&:hover": {
                        bgcolor: secondaryColor,
                        boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      textTransform: "none",
                      transition: "all 0.3s ease",
                      mt: 2
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                )}

                {forgotPassword && otpSent && (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        New Password
                      </Typography>
                      <Tooltip
                        title="Enter your new password"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "text.secondary", p: 0 }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (newPasswordError) setNewPasswordError("");
                      }}
                      onBlur={validateNewPassword}
                      placeholder="Enter your new password here"
                      error={!!newPasswordError}
                      helperText={
                        newPasswordError ||
                        (newPassword
                          ? "Password must be 8 characters, a number and a special character"
                          : "")
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: newPasswordError ? "#d32f2f" : "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: newPasswordError
                              ? "#d32f2f"
                              : primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: newPasswordError
                              ? "#d32f2f"
                              : primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                        "& .MuiFormHelperText-root": {
                          margin: "4px 0 0 0",
                          fontSize: "0.75rem",
                          color: newPasswordError ? "#d32f2f" : "text.secondary",
                        },
                        mb: 1.5
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              sx={{ color: "text.secondary" }}
                            >
                              {showNewPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Confirm Password
                      </Typography>
                      <Tooltip
                        title="Confirm your new password"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "text.secondary", p: 0 }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (confirmPasswordError) setConfirmPasswordError("");
                      }}
                      onBlur={validateConfirmPassword}
                      placeholder="Confirm your new password here"
                      error={!!confirmPasswordError}
                      helperText={confirmPasswordError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: confirmPasswordError
                              ? "#d32f2f"
                              : "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: confirmPasswordError
                              ? "#d32f2f"
                              : primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: confirmPasswordError
                              ? "#d32f2f"
                              : primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                        "& .MuiFormHelperText-root": {
                          margin: "4px 0 0 0",
                          fontSize: "0.75rem",
                        },
                        mb: 1.5
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              sx={{ color: "text.secondary" }}
                            >
                              {showConfirmPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        OTP
                      </Typography>
                      <Tooltip
                        title="Enter the verification code sent to your email"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "text.secondary", p: 0 }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (otpError) setOtpError("");
                      }}
                      onBlur={validateOtp}
                      placeholder="Enter verification code here"
                      error={!!otpError}
                      helperText={otpError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: otpError ? "#d32f2f" : "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: otpError ? "#d32f2f" : primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: otpError ? "#d32f2f" : primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                        "& .MuiFormHelperText-root": {
                          margin: "4px 0 0 0",
                          fontSize: "0.75rem",
                        },
                        mb: 2
                      }}
                    />
                    <Button
                      variant="contained"
                      type="button"
                      onClick={handleResetPassword}
                      disabled={loading}
                      fullWidth
                      sx={{
                        borderRadius: "0px",
                        py: 1,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        bgcolor: primaryColor,
                        color: "white",
                        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                        "&:hover": {
                          bgcolor: secondaryColor,
                          boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                        },
                        textTransform: "none",
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </Box>
                )}

                {/* Forgot Username Flow */}
                {forgotUsername && !usernameOtpSent && !retrievedUsername && (
                  <Button
                    fullWidth
                    type="button"
                    variant="contained"
                    onClick={handleSendUsernameOTP}
                    sx={{
                      borderRadius: "0px",
                      py: 1.2,
                      fontSize: "1rem",
                      fontWeight: 600,
                      bgcolor: primaryColor,
                      color: "white",
                      boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                      "&:hover": {
                        bgcolor: secondaryColor,
                        boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      textTransform: "none",
                      transition: "all 0.3s ease",
                      mt: 2
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                )}

                {forgotUsername && usernameOtpSent && !retrievedUsername && (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="OTP"
                      variant="outlined"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (otpError) setOtpError("");
                      }}
                      onBlur={validateOtp}
                      placeholder="Enter verification code"
                      error={!!otpError}
                      helperText={otpError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: otpError ? "#d32f2f" : "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: otpError ? "#d32f2f" : primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: otpError ? "#d32f2f" : primaryColor,
                          },
                        }
                      }}
                    />
                    <Button
                      fullWidth
                      type="button"
                      variant="contained"
                      onClick={handleVerifyUsernameOTP}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        bgcolor: primaryColor,
                        color: "white",
                        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                        "&:hover": {
                          bgcolor: secondaryColor,
                          boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & Retrieve"}
                    </Button>
                  </Box>
                )}



                {/* Login Buttons */}
                {!forgotPassword && !forgotUsername && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 0.5,
                    }}
                  >
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={needs2FA ? handle2FAVerification : handleLogin}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        bgcolor: primaryColor,
                        color: "white",
                        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                        "&:hover": {
                          bgcolor: secondaryColor,
                          boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : needs2FA ? (
                        "Verify Code"
                      ) : (
                        "Sign In"
                      )}
                    </Button>

                    {/* Register Button */}
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/first-time-register")}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        borderColor: primaryColor,
                        color: primaryColor,
                        borderWidth: "2px",
                        "&:hover": {
                          borderColor: secondaryColor,
                          backgroundColor: "rgba(51, 51, 51, 0.08)",
                          borderWidth: "2px",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      Create Account
                    </Button>

                  </Box>
                )}
              </>
            )}

            {retrievedUsername && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  Your Username is:
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: primaryColor, mb: 4 }}>
                  {retrievedUsername}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleBackToLogin}
                  sx={{
                    borderRadius: "0px",
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    bgcolor: primaryColor,
                    color: "white",
                    boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                    "&:hover": {
                      bgcolor: secondaryColor,
                      boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    textTransform: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Calculator Tab at Bottom */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          pr: 2,
          gap: 1,
          flexWrap: "wrap",
          pointerEvents: "none",
        }}
      >
        {/* Support Tab */}
        <Box
          onClick={() => {
            setOpenSupport(true);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            pointerEvents: "auto",
            minWidth: "300px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "260px",
              maxWidth: "340px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "220px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <EmailIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Contact Support
          </Typography>
        </Box>

        {/* Calculator Tab */}
        <Box
          onClick={() => setOpenCalculator(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            pointerEvents: "auto",
            minWidth: "320px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "280px",
              maxWidth: "350px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "240px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <CalculatorIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Use our Bond Premium Calculator
          </Typography>
        </Box>
      </Box>
      </Box>

      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context="Login"
      />

      {/* Account Selection Dialog */}
      <Dialog
        open={showAccountSelection}
        onClose={() => setShowAccountSelection(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "20px",
            padding: 2,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(51, 51, 51, 0.1)",
                color: primaryColor,
                width: 56,
                height: 56,
                margin: "0 auto",
                mb: 2,
              }}
            >
              <PersonIcon fontSize="large" />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Select Account
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Multiple accounts found for this email. Please select the account you want to recover.
            </Typography>
          </Box>
          <List sx={{ width: "100%", bgcolor: "background.paper" }}>
            {accountsList.map((account) => (
              <React.Fragment key={account.user_id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleAccountSelect(account.user_id)}
                    sx={{
                      borderRadius: "12px",
                      mb: 1,
                      "&:hover": {
                        backgroundColor: "rgba(51, 51, 51, 0.05)",
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: primaryColor }}>
                        {account.username.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={account.full_name || account.username}
                      secondary={
                        account.role || "User"
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => setShowAccountSelection(false)}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Components remain the same */}
      <Dialog
        open={openCalculator}
        onClose={() => setOpenCalculator(false)}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "20px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: "12px",
              color: primaryColor,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "rgba(51, 51, 51, 0.08)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </Box>
  );
};

export default Login;
